import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import AccountPendingDeletion from '../pages/AccountPendingDeletion';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

// Gate that renders children only for authenticated users (admins only when requireAdmin is set).
// Also routes between the client workspace and the consumer vault based on accountType.
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, userProfile, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the attempted location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && userProfile?.role !== 'admin') {
    return <Navigate to="/workspace" state={{ error: 'Admin access required' }} replace />;
  }

  const accountType: 'client' | 'consumer' = userProfile?.accountType ?? 'client';
  const isVaultRoute = location.pathname === '/vault' || location.pathname.startsWith('/vault/');

  // Consumers never see the client workspace/admin surface — send them to /vault.
  if (accountType === 'consumer' && !isVaultRoute) {
    return <Navigate to="/vault" replace />;
  }

  // Clients don't have a vault — send them to the workspace.
  if (accountType === 'client' && isVaultRoute) {
    return <Navigate to="/workspace" replace />;
  }

  // #452: the owner asked to delete the account; until the purge, signing in
  // shows only the choice to restore it (or download a copy, or sign out).
  if (accountType === 'client' && userProfile?.deletion) {
    return <AccountPendingDeletion />;
  }

  return <>{children}</>;
};
